import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, CalendarDays, Star, ExternalLink, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatEventDate, priceFromCents, isEventSoldOut } from "@/components/site/EventCard";

const STATUS_STYLES = {
  draft: "bg-slate-500/15 border-slate-500/40 text-slate-300",
  published: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  sold_out: "bg-svj-orange/15 border-svj-orange/40 text-svj-orange",
  cancelled: "bg-red-500/15 border-red-500/40 text-red-300",
  archived: "bg-svj-border/40 border-svj-border text-slate-400",
};

const STATUS_LABELS = {
  draft: "Concept",
  published: "Gepubliceerd",
  sold_out: "Uitverkocht",
  cancelled: "Geannuleerd",
  archived: "Gearchiveerd",
};

export default function AdminEvents() {
  const { authAxios, me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // An event_user manages exactly one existing event — creating or deleting the
  // event itself is org-management, outside their granted scope (the backend
  // rejects both with require_manager), so those actions are hidden here too.
  const isEventUser = me?.role === "event_user";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get("/admin/events");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async (ev) => {
    if (!window.confirm(`"${ev.title}" verwijderen? Als er al bestellingen zijn wordt het event gearchiveerd i.p.v. verwijderd.`)) return;
    try {
      const { data } = await authAxios.delete(`/admin/events/${ev.id}`);
      toast.success(data.archived ? "Event gearchiveerd (er waren al bestellingen)" : "Event verwijderd");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const onToggleFeatured = async (ev) => {
    try {
      await authAxios.patch(`/admin/events/${ev.id}`, { featured: !ev.featured });
      toast.success(ev.featured ? "Niet meer uitgelicht" : "Uitgelicht gezet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const filtered = items.filter((ev) => {
    if (statusFilter !== "all" && ev.status !== statusFilter) return false;
    const q = query.trim().toLowerCase();
    if (q) {
      const hay = [ev.title, ev.location, ev.organizations?.name].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div data-testid="admin-events-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Beheer</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">Events</h1>
          <p className="mt-1 text-sm text-slate-400">
            {isEventUser
              ? "Je eigen event."
              : me?.is_superadmin
              ? "Je ziet events van alle organisaties."
              : `Events van ${me?.organization?.name || "je organisatie"}.`}
          </p>
        </div>
        {!isEventUser && (
          <Link
            to="/admin/events/new"
            data-testid="admin-events-new"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2.5 px-5 text-sm hover:shadow-[0_0_24px_rgba(214,61,122,0.4)] transition-all"
          >
            <Plus className="h-4 w-4" /> Nieuw event
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op titel, plaats of organisatie…"
            data-testid="admin-events-search"
            className="w-full h-11 pl-11 pr-4 rounded-full bg-svj-card border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          {[["all", "Alle"], ["draft", "Concept"], ["published", "Gepubliceerd"], ["sold_out", "Uitverkocht"], ["cancelled", "Geannuleerd"], ["archived", "Gearchiveerd"]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setStatusFilter(k)}
              data-testid={`admin-events-filter-${k}`}
              className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                statusFilter === k
                  ? "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white border-transparent"
                  : "bg-svj-card border-svj-border text-slate-300 hover:text-white"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-svj-border overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-svj-card/60 text-[11px] uppercase tracking-widest text-slate-400 font-semibold border-b border-svj-border">
          <div className="col-span-4">Event</div>
          <div className="col-span-2">Datum</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Tickets</div>
          <div className="col-span-2 text-right">Acties</div>
        </div>
        {loading && (
          <div className="p-8 text-slate-400 text-sm" data-testid="admin-events-loading">Laden…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center" data-testid="admin-events-empty">
            <CalendarDays className="h-8 w-8 text-svj-cyan mx-auto" />
            <div className="mt-3 text-white font-semibold">Nog geen events</div>
            <p className="mt-1 text-sm text-slate-400">Maak je eerste event aan.</p>
          </div>
        )}
        {!loading && filtered.map((ev) => {
          const totalCapacity = (ev.ticket_types || []).reduce((a, t) => a + (t.capacity ?? 0), 0);
          const totalSold = (ev.ticket_types || []).reduce((a, t) => a + (t.sold_count ?? 0), 0);
          return (
            <div
              key={ev.id}
              data-testid={`admin-event-row-${ev.id}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-4 items-center border-b border-svj-border last:border-b-0 bg-svj-card"
            >
              <div className="md:col-span-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-svj-bg border border-svj-border overflow-hidden shrink-0">
                    {ev.cover_image_url && (
                      <img src={ev.cover_image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-white font-semibold truncate flex items-center gap-2">
                      {ev.title}
                      {ev.featured && <Star className="h-3.5 w-3.5 text-svj-orange fill-svj-orange shrink-0" />}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {ev.organizations?.name || "—"}
                      {ev.location ? ` · ${ev.location}` : ""}
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 text-sm text-slate-300">{formatEventDate(ev.starts_at)}</div>
              <div className="md:col-span-2">
                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[ev.status] || STATUS_STYLES.draft}`}>
                  {STATUS_LABELS[ev.status] || ev.status}
                </span>
              </div>
              <div className="md:col-span-2 text-sm">
                <span className="text-white font-semibold">{totalSold}</span>
                <span className="text-slate-400"> / {totalCapacity || "∞"}</span>
              </div>
              <div className="md:col-span-2 flex md:justify-end items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onToggleFeatured(ev)}
                  data-testid={`admin-event-star-${ev.id}`}
                  title="Uitgelicht"
                  className={`h-9 w-9 rounded-lg border flex items-center justify-center transition-colors ${
                    ev.featured
                      ? "bg-svj-orange/20 border-svj-orange/60 text-svj-orange"
                      : "bg-svj-bg border-svj-border text-slate-400 hover:text-svj-orange"
                  }`}
                >
                  <Star className="h-4 w-4" />
                </button>
                {ev.status === "published" && (
                  <Link
                    to={`/events/${ev.slug || ev.id}`}
                    target="_blank"
                    className="h-9 w-9 rounded-lg border bg-svj-bg border-svj-border text-slate-400 hover:text-white flex items-center justify-center"
                    title="Bekijk publiek"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                )}
                <Link
                  to={`/admin/events/${ev.id}`}
                  data-testid={`admin-event-edit-${ev.id}`}
                  className="h-9 w-9 rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white flex items-center justify-center"
                  title="Bewerken"
                >
                  <Pencil className="h-4 w-4" />
                </Link>
                {!isEventUser && (
                  <button
                    type="button"
                    onClick={() => onDelete(ev)}
                    data-testid={`admin-event-delete-${ev.id}`}
                    className="h-9 w-9 rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 flex items-center justify-center"
                    title="Verwijderen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
